import { useState, useRef, DragEvent } from 'react';
import { scanFilesForPhi, PhiScanResult } from '../utils/phiScan';

export interface UploadedFile {
  name: string;
  size: number;
  status: 'pending' | 'uploading' | 'ready' | 'error';
  errorMessage?: string;
}

interface FileUploadZoneProps {
  uploadedFiles: UploadedFile[];
  onFilesSelected: (files: File[]) => void;
  maxFiles?: number;
  disabled?: boolean;
}

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.csv'];

export function FileUploadZone({ uploadedFiles, onFilesSelected, maxFiles = 30, disabled }: FileUploadZoneProps) {
  const [dragging, setDragging] = useState(false);
  const [phiResult, setPhiResult] = useState<PhiScanResult | null>(null);
  const [phiAcknowledged, setPhiAcknowledged] = useState(false);
  // Files held back until the user acknowledges the PHI warning
  const [heldFiles, setHeldFiles] = useState<File[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filterValidFiles = (incoming: File[]) => {
    return incoming.filter(f => {
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      return ACCEPTED_EXTENSIONS.includes(ext);
    });
  };

  const addFiles = async (incoming: File[]) => {
    if (disabled) return;
    setNotice(null);
    const valid = filterValidFiles(incoming);
    const rejectedCount = incoming.length - valid.length;
    if (rejectedCount > 0) {
      setNotice(`${rejectedCount} file${rejectedCount === 1 ? ' was' : 's were'} skipped — only PDF, DOCX, TXT, and CSV are supported.`);
    }
    if (valid.length === 0) return;

    // Check limit
    if (uploadedFiles.length + heldFiles.length + valid.length > maxFiles) {
      setNotice(`Cannot add ${valid.length} file${valid.length === 1 ? '' : 's'} — the limit is ${maxFiles} files.`);
      return;
    }

    // Run PHI scan on text files. If indicators are found, HOLD the whole
    // batch until the user explicitly acknowledges — do not upload yet.
    const result = await scanFilesForPhi(valid);
    if (result.hasPhiIndicators) {
      setPhiResult(prev => prev
        ? { hasPhiIndicators: true, flaggedFiles: [...new Set([...prev.flaggedFiles, ...result.flaggedFiles])] }
        : result);
      setPhiAcknowledged(false);
      setHeldFiles(prev => [...prev, ...valid]);
      return;
    }

    onFilesSelected(valid);
  };

  const confirmHeldFiles = () => {
    if (!phiAcknowledged || heldFiles.length === 0) return;
    onFilesSelected(heldFiles);
    setHeldFiles([]);
    setPhiResult(null);
    setPhiAcknowledged(false);
  };

  const cancelHeldFiles = () => {
    setHeldFiles([]);
    setPhiResult(null);
    setPhiAcknowledged(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const totalFiles = uploadedFiles.length;
  const uploadingCount = uploadedFiles.filter(f => f.status === 'uploading').length;

  return (
    <div>
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-btn p-8 text-center transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' :
          dragging ? 'border-primary-400 bg-primary-50 cursor-pointer' : 'border-neutral-300 hover:border-neutral-400 cursor-pointer'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={e => { addFiles(Array.from(e.target.files || [])); if (inputRef.current) inputRef.current.value = ''; }}
          className="hidden"
          aria-label="Upload files"
          disabled={disabled}
        />
        <svg className="w-8 h-8 mx-auto text-neutral-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className="text-sm text-neutral-500">Drop files here or click to browse</p>
        <p className="text-xs text-neutral-400 mt-1">PDF, DOCX, TXT, CSV · {totalFiles}/{maxFiles} files</p>
        <p className="text-xs text-neutral-300 mt-1">Images & other formats coming soon</p>
      </div>

      {/* Skipped/limit notices */}
      {notice && (
        <p className="mt-2 text-xs text-red-500" role="alert">{notice}</p>
      )}

      {/* Upload progress summary */}
      {uploadingCount > 0 && (
        <div className="mt-2 flex items-center gap-2 text-xs text-primary-600">
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Uploading {uploadingCount} {uploadingCount === 1 ? 'file' : 'files'}...
        </div>
      )}

      {/* PHI warning — blocks upload of held files until acknowledged */}
      {phiResult?.hasPhiIndicators && heldFiles.length > 0 && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-btn">
          <p className="text-xs text-yellow-800 font-medium">Potential patient identifiers detected — upload paused</p>
          <p className="text-xs text-yellow-700 mt-1">
            Found in: {phiResult.flaggedFiles.join(', ')}
          </p>
          <p className="text-xs text-yellow-700 mt-1">
            Files will be de-identified automatically before storage, but please confirm you intend to upload them.
          </p>
          <label className="flex items-center gap-2 mt-2 text-xs text-yellow-800 cursor-pointer">
            <input
              type="checkbox"
              checked={phiAcknowledged}
              onChange={e => setPhiAcknowledged(e.target.checked)}
              className="rounded"
            />
            I confirm these files are de-identified or I accept the risk
          </label>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={confirmHeldFiles}
              disabled={!phiAcknowledged}
              className="px-3 py-1 text-xs font-medium rounded-btn bg-yellow-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-yellow-700 transition-colors"
            >
              Upload {heldFiles.length} {heldFiles.length === 1 ? 'file' : 'files'}
            </button>
            <button
              type="button"
              onClick={cancelHeldFiles}
              className="px-3 py-1 text-xs font-medium rounded-btn text-yellow-800 hover:bg-yellow-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* PDF reminder */}
      {uploadedFiles.some(f => f.name.endsWith('.pdf')) && !phiResult?.hasPhiIndicators && (
        <p className="mt-2 text-xs text-neutral-400">
          Please ensure PDF files do not contain unredacted patient information.
        </p>
      )}

      {/* File list with status */}
      {uploadedFiles.length > 0 && (
        <ul className="mt-3 space-y-1">
          {uploadedFiles.map((file, i) => (
            <li key={i} className="flex items-center justify-between py-1.5 px-3 bg-neutral-50 rounded-btn text-xs">
              <span className="text-neutral-700 truncate">{file.name}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-neutral-400">{(file.size / 1024).toFixed(0)} KB</span>
                {file.status === 'pending' && (
                  <span className="text-neutral-400">queued</span>
                )}
                {file.status === 'uploading' && (
                  <svg className="w-3.5 h-3.5 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {file.status === 'ready' && (
                  <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {file.status === 'error' && (
                  <span className="text-red-400" title={file.errorMessage}>!</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
